<template>
    <section
        v-if="!(breakpoints?.isMobile && slice.primary.hideInMobile) && slice.primary"
        :id="slice.primary.anchorName || undefined"
        ref="section"
        class="slice cta-wrapper"
        :class="{
            'slice-cta--trends': slice.variation === 'trendsCta',
            plain: slice.variation === 'plainBackground',
            'theme-malt': theme === 'malt',
            'theme-strategy': theme === 'strategy',
            'theme-supermalter': theme === 'supermalter',
        }"
        aria-label="call-to-action"
    >
        <PrismicRichText :field="slice.primary.title" class="title" />
        <PrismicRichText v-if="slice.variation === 'plainBackground'" :field="slice.primary.description" class="description" />

        <div class="buttons-wrapper">
            <template v-if="slice.variation === 'trendsCta'">
                <VJoyButton
                    v-if="isFilledKeyText(slice.primary.ctaLink) && isFilledKeyText(slice.primary.ctaLinkText)"
                    :variant="ctaVariant"
                    :href="slice.primary.ctaLink"
                    data-component-source="call-to-action"
                >
                    {{ slice.primary.ctaLinkText }}
                </VJoyButton>
            </template>
            <template v-else>
                <VJoyButton
                    v-if="isFilledKeyText(slice.primary.primaryCtaLink) && isFilledKeyText(slice.primary.primaryCtaLinkText)"
                    :variant="ctaVariant"
                    :href="slice.primary.primaryCtaLink"
                    data-component-source="call-to-action"
                >
                    {{ slice.primary.primaryCtaLinkText }}
                </VJoyButton>

                <VJoyButton
                    v-if="isFilledKeyText(slice.primary.secondaryCtaLink) && isFilledKeyText(slice.primary.secondaryCtaLinkText)"
                    variant="ghost"
                    :href="slice.primary.secondaryCtaLink"
                    data-component-source="call-to-action"
                >
                    {{ slice.primary.secondaryCtaLinkText }}
                </VJoyButton>
            </template>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef} from 'vue';
    import type {Content} from '@prismicio/client';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import {useComponentTracking} from '#imports';
    import {VJoyButton, type TJoyButtonVariants} from '@maltjoy/core-vue';
    import {useBreakpoints} from '../../composables/shared/useBreakpoints';
    import {usePrismicTypeGuards} from '../../composables/shared/useTypeGuards';

    const section = useTemplateRef('section');
    const breakpoints = useBreakpoints();
    const {isFilledKeyText} = usePrismicTypeGuards();

    const props = defineProps(getSliceComponentProps<Content.CallToActionSliceSlice>());

    const isMaltStrategyTheme = props.slice.variation === 'default' && props.slice.primary.maltStrategyTheme;
    const primaryTheme = props.slice.variation === 'default' || props.slice.variation === 'plainBackground' ? props.slice.primary.theme : '';
    const theme = ref(isMaltStrategyTheme ? 'strategy' : primaryTheme);
    const ctaVariants = ref({
        malt: 'primary',
        strategy: 'main',
        supermalter: 'admin',
    });

    const ctaVariant = computed<TJoyButtonVariants>(() => ctaVariants.value[theme.value as keyof typeof ctaVariants.value] as TJoyButtonVariants);

    useComponentTracking(section);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '../../scss/variables.scss' as vars;

    .cta-wrapper {
        @include vars.flex-column-center;

        $background-width: 1658px;
        $visible-background-width: 1338px;

        width: 100%;
        padding: 100px 20px 90px;
        font-family: var(--joy-font-family-base);
        background-repeat: no-repeat;
        background-size: auto 590px;
        min-height: 590px;

        &.slice-cta--trends {
            overflow: hidden;
        }

        &:not(.slice-cta--trends) {
            background-image: url('./cta-shapes.svg');
        }

        // lots of breakpoints to avoid white text and white button to be out of the shape while preserving ratio as much as possible

        // if screen is too big, center the shape
        background-position: center;

        // if screen is small enough, stick the shape to the left, but cut it less if screen is still quite big
        @media (max-width: 1920px) {
            --background-position: center right calc(100vw - #{$visible-background-width} - 100px);

            background-position: var(--background-position);
        }

        // when space on the right starts to be normal, cut the shape more on the left
        @media (max-width: calc(#{$visible-background-width} / 0.80 + 100px)) {
            --background-position: center right 20vw;

            background-position: var(--background-position);
        }

        // when shape is properly cut, reduce space on the right
        @media (max-width: calc(#{$visible-background-width} / 0.80)) {
            --background-position: center right calc(100vw - #{$visible-background-width});

            background-position: var(--background-position);
        }

        // cut the left of the shape if it is too big to avoid the right part beeing cut
        @media (max-width: $visible-background-width) {
            background-position: center right;
        }

        // center and cut on mobile
        @media (max-width: vars.$mobile-breakpoint) {
            background-position: center;
            min-height: unset;
            background-size: auto 100%;
            padding: 125px vars.$margin-section-mobile 110px;
        }

        .title h2 {
            text-align: center;
            color: var(--joy-color-neutral-0);

            @media (max-width: vars.$mobile-breakpoint) {
                margin-bottom: 32px;
            }
        }

        .buttons-wrapper {
            width: 100%;

            > a {
                width: 100%;
                margin-bottom: var(--joy-core-spacing-5);
            }

            @include mq.screen_sm(min) {
                display: flex;
                justify-content: center;
                gap: var(--joy-core-spacing-5);
                max-width: 620px;
                margin: 0 auto;

                > a {
                    width: auto;
                    margin-bottom: 0;
                }
            }
        }

        &:not(.plain, .slice-cta--trends) {
            .title h2 {
                max-width: 620px;
                margin-bottom: 48px;

                @include vars.secondary-title;

                color: var(--joy-color-neutral-0);
            }
        }

        &.plain {
            padding: 110px 0;
            min-height: unset;
            background-image: none;
            background-color: var(--joy-color-primary-50);

            @media (max-width: vars.$mobile-breakpoint) {
                padding: 50px 0;
            }

            .title {
                @include vars.content-width;

                h2 {
                    margin-bottom: var(--joy-core-spacing-6);
                }
            }

            .description {
                margin-bottom: var(--joy-core-spacing-10);
                text-align: center;

                @include vars.content-width;

                * {
                    color: var(--joy-color-neutral-0);
                }
            }

            .buttons-wrapper {
                justify-content: center;

                @include vars.content-width;

                @media (max-width: vars.$mobile-breakpoint) {
                    padding: 0 20px;
                }
            }
        }

        &.slice-cta {
            &--trends {
                &::before {
                    content: '';
                    position: absolute;
                    left: 0;
                    top: 70px;
                    width: 100%;
                    height: 100%;
                    background: url('./cta-shape-trends.svg') no-repeat top left;
                    background-size: 50%;
                    z-index: -1;

                    @include mq.screen_xs(min) {
                        top: 40px;
                    }

                    @include mq.screen_sm(min) {
                        top: 20px;
                        background-size: auto;
                    }
                }

                &::after {
                    content: '';
                    position: absolute;
                    top: 50%;
                    left: 42%;
                    height: 60%;
                    width: 115%;
                    background: var(--joy-color-primary-50);
                    border-radius: 0 220px 220px 0;
                    transform: translate(-50%, -50%) rotate(-4deg);
                    z-index: -1;

                    @include mq.screen_sm(min) {
                        height: 80%;
                    }

                    @include mq.screen_lg(min) {
                        width: 90%;
                    }
                }

                .buttons-wrapper {
                    width: initial;

                    .joy-button {
                        background-color: transparent;
                        border-color: var(--joy-color-neutral-10);
                        font-size: var(--joy-font-size-primary-600);
                        padding-left: var(--joy-core-spacing-5);
                        padding-right: var(--joy-core-spacing-5);

                        &:hover {
                            background-color: var(--joy-color-primary-70);
                        }

                        @include mq.screen_sm(min) {
                            padding: var(--joy-core-spacing-5) var(--joy-core-spacing-9);
                        }
                    }
                }

                .title {
                    font-family: var(--joy-font-family-title);
                    font-weight: var(--joy-font-weight-normal);
                    font-size: 40px;

                    h1,
                    h2 {
                        font-size: inherit;
                        color: var(--joy-color-neutral-0);
                        font-weight: inherit;
                        margin-bottom: var(--joy-core-spacing-4);

                        @include mq.screen_lg(min) {
                            margin-bottom: var(--joy-core-spacing-6);
                        }
                    }

                    @include mq.screen_lg(min) {
                        font-size: var(--joy-font-size-secondary-800);
                    }

                    strong {
                        display: inline-block;
                        position: relative;
                        z-index: 1;
                        font-weight: var(--joy-font-weight-normal);
                        font-family: var(--joy-font-family-title);
                        line-height: var(--joy-line-height-small);
                        color: var(--joy-color-neutral-0);
                        padding: 0 var(--joy-core-spacing-4);

                        &::before {
                            content: '';
                            display: inline-block;
                            position: absolute;
                            left: 0;
                            top: -0.09em;
                            height: 1em;
                            width: 100%;
                            background-color: var(--joy-color-brand-primary-30);
                            border-radius: 9999px;
                            z-index: -1;
                        }
                    }
                }
            }
        }

        &.theme {
            &-strategy {
                &:not(.plain) {
                    background-image: url('./cta-shapes-ms.svg');
                }

                &.plain {
                    background-color: var(--joy-color-secondary-50);
                }
            }

            &-supermalter {
                &:not(.plain) {
                    background-image: url('./cta-shapes-supermalter.svg');
                }

                &.plain {
                    background-color: var(--joy-color-primary-50);
                }

                .joy-button_primary {
                    background-color: var(--joy-color-brand-secondary-30);
                }
            }
        }
    }
</style>
